import { AxiosInstance } from "axios";
import useSWR from "swr";

import { Campaign } from "models/CommonType";
import { errorFormatter } from 'services/serviceUtils/responseFormatter';
import { Error } from 'services/types/CommonTypes';
import { fetcherWithToken } from "./ApiClient";

export const useCampaign = (id: string, token: string) => {
  const { data, error, isLoading, mutate } = useSWR(
    !!id ? [`${process.env.REACT_APP_DOMAIN}/api/campaigns/${id}/`, token] : null,
    ([url, token]) => fetcherWithToken(url, token));

  return {
    data: data || {},
    isLoading: isLoading,
    isError: error,
    mutate: mutate,
  }
};

// Response type for join campaign (similar to SubmissionResponse)
export interface CampaignJoinResponse extends Campaign {
  error?: {
    message?: string;
    isDirty?: boolean;
    statusCode?: string | number;
    code?: string | number;
  };
}

export interface CampaignProps {
  joinCampaign: (id: string) => Promise<CampaignJoinResponse>;
  joinPrivateCampaign: (id: string, code: string, finalFee?: number) => Promise<Campaign>;
}

function createCampaignService(axiosClient: AxiosInstance): CampaignProps {
  return {
    joinCampaign: async (id: string): Promise<CampaignJoinResponse> => {
      return axiosClient.post(`/api/campaigns/${id}/join/`).then(
        (res) => res?.data,
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          return { error: formattedError };
        }
      );
    },

    joinPrivateCampaign: async (id: string, code: string, finalFee: number = 0): Promise<Campaign> => {
      let requestData;

      if (finalFee > 0) {
        requestData = {
          data: {
            attributes: {
              code: code,
              is_pay_confirmed: true
            }
          }
        };
      } else {
        requestData = {
          data: {
            attributes: {
              code: code
            }
          }
        };
      }

      return axiosClient.post(`/api/campaigns/${id}/join/?format=json`, requestData).then(
        (res) => res?.data,
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          throw formattedError;
        }
      );
    },
  };
}

export const useCampaignStats = (campaignId: string, token: string) => {
  const { data, error, isLoading, mutate } = useSWR(
    !!campaignId ? [`${process.env.REACT_APP_DOMAIN}/api/campaign-stats/?campaign=${campaignId}&sort=-modified`, token] : null,
    ([url, token]) => fetcherWithToken(url, token)
  );

  // API returns paginated response with data inside results array
  const stats = data?.results?.[0] || null;

  return {
    data: stats,
    isLoading,
    isError: error,
    mutate: mutate,
  };
};

export const useRelatedCampaigns = (userId: string, token: string) => {
  const { data, error, isLoading, mutate } = useSWR(
    !!userId && !!token ? [
      `${process.env.REACT_APP_DOMAIN}/api/campaigns/?user=${userId}&page[number]=1&page[size]=3&sort=campaign_order,-started&ia_active=true`, 
      token
    ] : null,
    ([url, token]) => fetcherWithToken(url, token)
  );

  return {
    data: data?.results || [],
    isLoading,
    isError: error,
    mutate: mutate,
  };
};

export default createCampaignService;